import {
  getApplications,
  getWorkflows,
  getWorkflow,
  createWorkflow,
  updateWorkflow,
  deleteWorkflow,
  activateWorkflow,
  deactivateWorkflow,
  publishWorkflow,
  startEditWorkflow,
  unlockWorkflow
} from './api';

import './admin.less';

const state = {
  page: 1,
  pageSize: 20,
  totalCount: 0,
  totalPages: 1,
  workflows: [],
  applications: [],
  currentAction: null,
  currentWorkflowId: null
};

// DOM elements
const elements = {
  loading: document.getElementById('loading'),
  error: document.getElementById('error'),
  tableBody: document.getElementById('workflows-body'),
  pageInfo: document.getElementById('page-info'),
  btnPrev: document.getElementById('btn-prev'),
  btnNext: document.getElementById('btn-next'),
  btnRefresh: document.getElementById('btn-refresh'),
  btnAdd: document.getElementById('btn-add-workflow'),
  searchInput: document.getElementById('search-input'),
  filterApplication: document.getElementById('filter-application'),
  filterActive: document.getElementById('filter-active'),
  filterStatus: document.getElementById('filter-status'),
  workflowModal: document.getElementById('workflow-modal'),
  confirmModal: document.getElementById('confirm-modal'),
  workflowForm: document.getElementById('workflow-form'),
  modalTitle: document.getElementById('modal-title'),
  formErrors: document.getElementById('form-errors'),
  confirmTitle: document.getElementById('confirm-title'),
  confirmMessage: document.getElementById('confirm-message'),
  btnConfirm: document.getElementById('btn-confirm'),
  detailsModal: document.getElementById('details-modal'),
  detailsTitle: document.getElementById('details-title'),
  detailsBody: document.getElementById('details-body')
};

async function init() {
  bindEvents();
  await loadApplications();
  loadWorkflows();
}

function bindEvents() {
  elements.btnRefresh.addEventListener('click', loadWorkflows);
  elements.btnAdd.addEventListener('click', () => openWorkflowModal());
  elements.btnPrev.addEventListener('click', () => changePage(state.page - 1));
  elements.btnNext.addEventListener('click', () => changePage(state.page + 1));

  elements.searchInput.addEventListener('input', debounce(loadWorkflows, 300));
  elements.filterApplication.addEventListener('change', loadWorkflows);
  elements.filterActive.addEventListener('change', loadWorkflows);
  elements.filterStatus.addEventListener('change', loadWorkflows);

  elements.workflowForm.addEventListener('submit', handleFormSubmit);

  document.querySelectorAll('[data-close]').forEach(btn => {
    btn.addEventListener('click', closeModals);
  });

  document.querySelectorAll('.modal-overlay').forEach(overlay => {
    overlay.addEventListener('click', closeModals);
  });

  elements.btnConfirm.addEventListener('click', handleConfirm);
}

function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

async function loadApplications() {
  const response = await getApplications();
  if (!response.success) {
    showError('Failed to load applications: ' + response.message);
    return;
  }

  state.applications = response.data || [];
  populateApplicationDropdowns();
}

function populateApplicationDropdowns() {
  const filterSelect = elements.filterApplication;
  const formSelect = document.getElementById('application');

  // Save current selections
  const filterValue = filterSelect.value;
  const formValue = formSelect.value;

  // Keep existing "All applications" / "Select an application" options
  filterSelect.innerHTML = '<option value="">All applications</option>';
  formSelect.innerHTML = '<option value="">Select an application</option>';

  state.applications.forEach(app => {
    const option = document.createElement('option');
    option.value = app.id;
    option.textContent = app.appName;
    option.dataset.name = app.appName;
    filterSelect.appendChild(option.cloneNode(true));
    formSelect.appendChild(option);
  });

  filterSelect.value = filterValue;
  formSelect.value = formValue;
}

async function loadWorkflows() {
  showLoading(true);
  hideError();

  const params = {
    page: state.page,
    pageSize: state.pageSize,
    search: elements.searchInput.value.trim(),
    appId: elements.filterApplication.value,
    isActive: elements.filterActive.value,
    developmentStatus: elements.filterStatus.value,
    sortBy: 'createdDate',
    sortDirection: 'desc'
  };

  const response = await getWorkflows(params);

  showLoading(false);

  if (!response.success) {
    showError(response.message);
    return;
  }

  const data = response.data;
  state.workflows = data.items || [];
  state.totalCount = data.totalCount || 0;
  state.totalPages = data.totalPages || 1;

  renderTable();
  updatePagination();
}

function renderTable() {
  if (state.workflows.length === 0) {
    elements.tableBody.innerHTML = `
      <tr class="empty-row">
        <td colspan="8">No workflows found.</td>
      </tr>
    `;
    return;
  }

  elements.tableBody.innerHTML = state.workflows.map(workflow => `
    <tr>
      <td>${escapeHtml(workflow.appName)}</td>
      <td>${escapeHtml(workflow.workflowName)}</td>
      <td>${escapeHtml(workflow.processId || '')}</td>
      <td>${escapeHtml(workflow.version || '')}</td>
      <td>${renderActiveBadge(workflow.isActive)}</td>
      <td>${renderStatusBadge(workflow.developmentStatus)}</td>
      <td>${renderInEditBadge(workflow.isInEdit)}</td>
      <td class="actions-cell">${renderActions(workflow)}</td>
    </tr>
  `).join('');
}

function renderActions(workflow) {
  const isDraft = workflow.developmentStatus === 'Draft';
  const isPublished = workflow.developmentStatus === 'Published';
  const isActive = workflow.isActive === 'Y';
  const isInactive = workflow.isActive === 'N';
  const isInEdit = workflow.isInEdit === 'Y';

  let buttons = `<button class="btn btn-xs btn-primary" onclick="window.openWorkflow(${workflow.id})">Open</button>`;
  buttons += `<button class="btn btn-xs btn-info" onclick="window.showWorkflowDetails(${workflow.id})">Details</button>`;

  if (isInEdit) {
    buttons += `<button class="btn btn-xs btn-warning" onclick="window.unlockWorkflow(${workflow.id})">Unlock</button>`;
  }

  if (isDraft) {
    buttons += `<button class="btn btn-xs btn-secondary" onclick="window.editWorkflow(${workflow.id})">Edit</button>`;
    buttons += `<button class="btn btn-xs btn-success" onclick="window.publishWorkflow(${workflow.id})">Publish</button>`;
    buttons += `<button class="btn btn-xs btn-danger" onclick="window.deleteWorkflow(${workflow.id})">Delete</button>`;
  }

  if (isPublished) {
    if (isInactive) {
      buttons += `<button class="btn btn-xs btn-success" onclick="window.activateWorkflow(${workflow.id})">Activate</button>`;
    }
    if (isActive) {
      buttons += `<button class="btn btn-xs btn-warning" onclick="window.deactivateWorkflow(${workflow.id})">Deactivate</button>`;
    }
  }

  return buttons;
}

function renderInEditBadge(isInEdit) {
  if (isInEdit === 'Y') {
    return `<span class="lock-icon" title="In edit">&#128274;</span>`;
  }
  return '';
}

function renderActiveBadge(isActive) {
  if (isActive === 'Y') {
    return `<span class="status-badge status-active">Yes</span>`;
  }
  if (isActive === 'N') {
    return `<span class="status-badge status-inactive">No</span>`;
  }
  return '';
}

function renderStatusBadge(status) {
  if (status === 'Draft') {
    return `<span class="status-badge status-draft">Draft</span>`;
  }
  if (status === 'Published') {
    return `<span class="status-badge status-published">Published</span>`;
  }
  return escapeHtml(status || '');
}

function updatePagination() {
  elements.pageInfo.textContent = `Page ${state.page} of ${state.totalPages}`;
  elements.btnPrev.disabled = state.page <= 1;
  elements.btnNext.disabled = state.page >= state.totalPages;
}

function changePage(page) {
  if (page < 1 || page > state.totalPages) return;
  state.page = page;
  loadWorkflows();
}

function showLoading(show) {
  elements.loading.classList.toggle('hidden', !show);
}

function showError(message) {
  elements.error.textContent = message;
  elements.error.classList.remove('hidden');
}

function hideError() {
  elements.error.classList.add('hidden');
  elements.error.textContent = '';
}

function openWorkflowModal(workflow = null) {
  elements.formErrors.innerHTML = '';
  elements.workflowForm.reset();

  const applicationSelect = document.getElementById('application');
  applicationSelect.disabled = false;
  applicationSelect.title = '';

  if (workflow) {
    elements.modalTitle.textContent = 'Edit Workflow';
    document.getElementById('workflow-id').value = workflow.id;
    applicationSelect.value = workflow.appId || '';
    document.getElementById('workflow-name').value = workflow.workflowName || '';
    document.getElementById('process-id').value = workflow.processId || '';
    document.getElementById('description').value = workflow.description || '';
    document.getElementById('major-version').value = workflow.majorVersion ?? 1;
    document.getElementById('minor-version').value = workflow.minorVersion ?? 0;
    document.getElementById('mapping-process-id').value = workflow.mappingProcessId || '';
    document.getElementById('is-datacapture').checked = workflow.isDatacaptureProcess || false;
    document.getElementById('datacapture-project-name').value = workflow.datacaptureProjectName || '';

    if (workflow.developmentStatus === 'Published') {
      applicationSelect.disabled = true;
      applicationSelect.title = 'Application cannot be changed after the workflow has been published.';
    }
  } else {
    elements.modalTitle.textContent = 'Add Workflow';
    document.getElementById('workflow-id').value = '';
    applicationSelect.value = '';
    document.getElementById('major-version').value = 1;
    document.getElementById('minor-version').value = 0;
  }

  elements.workflowModal.classList.remove('hidden');
}

async function handleFormSubmit(event) {
  event.preventDefault();
  elements.formErrors.innerHTML = '';

  const id = document.getElementById('workflow-id').value;
  const applicationSelect = document.getElementById('application');
  const selectedOption = applicationSelect.options[applicationSelect.selectedIndex];
  const appId = parseInt(applicationSelect.value, 10);
  const appName = selectedOption ? selectedOption.dataset.name || selectedOption.textContent : '';

  const workflow = {
    appId: appId,
    appName: appName,
    workflowName: document.getElementById('workflow-name').value.trim(),
    processId: document.getElementById('process-id').value.trim() || null,
    description: document.getElementById('description').value.trim() || null,
    majorVersion: parseFloat(document.getElementById('major-version').value) || 0,
    minorVersion: parseFloat(document.getElementById('minor-version').value) || 0,
    mappingProcessId: document.getElementById('mapping-process-id').value.trim() || null,
    isDatacaptureProcess: document.getElementById('is-datacapture').checked,
    datacaptureProjectName: document.getElementById('datacapture-project-name').value.trim() || null
  };

  const response = id
    ? await updateWorkflow(parseInt(id, 10), workflow)
    : await createWorkflow(workflow);

  if (!response.success) {
    showFormErrors(response);
    return;
  }

  closeModals();
  loadWorkflows();
}

function showFormErrors(response) {
  const errors = response.errors || [];
  if (errors.length > 0) {
    elements.formErrors.innerHTML = `<ul>${errors.map(e => `<li>${escapeHtml(e)}</li>`).join('')}</ul>`;
  } else if (response.message) {
    elements.formErrors.innerHTML = `<p>${escapeHtml(response.message)}</p>`;
  }
}

function openConfirmModal(title, message, action, workflowId) {
  state.currentAction = action;
  state.currentWorkflowId = workflowId;
  elements.confirmTitle.textContent = title;
  elements.confirmMessage.textContent = message;
  elements.confirmModal.classList.remove('hidden');
}

async function handleConfirm() {
  if (!state.currentAction || !state.currentWorkflowId) return;

  const id = state.currentWorkflowId;
  let response;

  switch (state.currentAction) {
    case 'delete':
      response = await deleteWorkflow(id);
      break;
    case 'activate':
      response = await activateWorkflow(id);
      break;
    case 'deactivate':
      response = await deactivateWorkflow(id);
      break;
    case 'publish':
      response = await publishWorkflow(id, null);
      break;
    default:
      return;
  }

  closeModals();

  if (!response.success) {
    showError(response.message);
    return;
  }

  loadWorkflows();
}

function openDetailsModal(workflow) {
  elements.detailsTitle.textContent = `Details: ${escapeHtml(workflow.workflowName)}`;
  elements.detailsBody.innerHTML = `
    <div class="details-row"><span class="details-label">Application:</span> <span class="details-value">${escapeHtml(workflow.appName)}</span></div>
    <div class="details-row"><span class="details-label">Workflow Name:</span> <span class="details-value">${escapeHtml(workflow.workflowName)}</span></div>
    <div class="details-row"><span class="details-label">Process ID:</span> <span class="details-value">${escapeHtml(workflow.processId || '')}</span></div>
    <div class="details-row"><span class="details-label">Version:</span> <span class="details-value">${escapeHtml(workflow.version || '')}</span></div>
    <div class="details-row"><span class="details-label">Active:</span> <span class="details-value">${workflow.isActive === 'Y' ? 'Yes' : 'No'}</span></div>
    <div class="details-row"><span class="details-label">Development Status:</span> <span class="details-value">${escapeHtml(workflow.developmentStatus || '')}</span></div>
    <div class="details-row"><span class="details-label">In Edit:</span> <span class="details-value">${workflow.isInEdit === 'Y' ? 'Yes' : 'No'}</span></div>
    <div class="details-row"><span class="details-label">Datacapture Process:</span> <span class="details-value">${workflow.isDatacaptureProcess ? 'Yes' : 'No'}</span></div>
    <div class="details-row"><span class="details-label">Datacapture Project:</span> <span class="details-value">${escapeHtml(workflow.datacaptureProjectName || '')}</span></div>
    <div class="details-row"><span class="details-label">Mapping Process ID:</span> <span class="details-value">${escapeHtml(workflow.mappingProcessId || '')}</span></div>
    <div class="details-row"><span class="details-label">Created By:</span> <span class="details-value">${escapeHtml(workflow.createdBy || '')}</span></div>
    <div class="details-row"><span class="details-label">Created Date:</span> <span class="details-value">${formatDate(workflow.createdDate)}</span></div>
    <div class="details-row"><span class="details-label">Modified By:</span> <span class="details-value">${escapeHtml(workflow.modifiedBy || '')}</span></div>
    <div class="details-row"><span class="details-label">Last Modified:</span> <span class="details-value">${formatDate(workflow.lastModifiedDate)}</span></div>
    <div class="details-row"><span class="details-label">Deployed Date:</span> <span class="details-value">${formatDate(workflow.deployedDate)}</span></div>
    <div class="details-row"><span class="details-label">Description:</span> <span class="details-value">${escapeHtml(workflow.description || '')}</span></div>
  `;
  elements.detailsModal.classList.remove('hidden');
}

function closeModals() {
  elements.workflowModal.classList.add('hidden');
  elements.confirmModal.classList.add('hidden');
  elements.detailsModal.classList.add('hidden');
  state.currentAction = null;
  state.currentWorkflowId = null;
}

// Global action handlers
window.openWorkflow = async function (id) {
  const response = await startEditWorkflow(id);
  if (!response.success) {
    showError(response.message);
    return;
  }
  window.location.href = `designer.html?workflowId=${id}&mode=view`;
};

window.showWorkflowDetails = async function (id) {
  const response = await getWorkflow(id);
  if (!response.success) {
    showError(response.message);
    return;
  }
  openDetailsModal(response.data);
};

window.unlockWorkflow = async function (id) {
  const response = await unlockWorkflow(id);
  if (!response.success) {
    showError(response.message);
    return;
  }
  loadWorkflows();
};

window.editWorkflow = async function (id) {
  const response = await getWorkflow(id);
  if (!response.success) {
    showError(response.message);
    return;
  }
  openWorkflowModal(response.data);
};

window.deleteWorkflow = function (id) {
  // Resolve the name from state instead of inlining it into the onclick
  // attribute: workflow names are user input and must never be interpolated
  // into HTML/JS (escapeHtml does not escape single quotes).
  const workflow = state.workflows.find(item => item.id === id);
  const name = workflow ? workflow.workflowName : '';

  openConfirmModal(
    'Delete Workflow',
    `Are you sure you want to delete workflow "${name}"?`,
    'delete',
    id
  );
};

window.activateWorkflow = function (id) {
  openConfirmModal(
    'Activate Workflow',
    'Are you sure you want to activate this workflow?',
    'activate',
    id
  );
};

window.deactivateWorkflow = function (id) {
  openConfirmModal(
    'Deactivate Workflow',
    'Are you sure you want to deactivate this workflow?',
    'deactivate',
    id
  );
};

window.publishWorkflow = function (id) {
  openConfirmModal(
    'Publish Workflow',
    'Publishing will create/update the deployed workflow definition and increment the minor version. Are you sure?',
    'publish',
    id
  );
};

function escapeHtml(text) {
  if (text === null || text === undefined) return '';
  const div = document.createElement('div');
  div.textContent = String(text);
  return div.innerHTML;
}

function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

// Initialize
document.addEventListener('DOMContentLoaded', init);
